import React, { Suspense } from "react";
import ReactDOM from "react-dom";
import "./assets/css/tailwind.output.css";
import "./assets/css/theme.css";
import "./i18n"; // initialize i18n before App renders
import App from "./App";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { SidebarProvider } from "./context/SidebarContext";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import ThemedSuspense from "./components/ThemedSuspense";
import { Windmill } from "@windmill/react-ui";
import * as serviceWorker from "./serviceWorker";

import {
  Chart,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import { GoogleOAuthProvider } from "@react-oauth/google";

Chart.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function MainApp() {
  const { theme } = useTheme();

  const muiTheme = React.useMemo(() => {
    const isDark = theme === "dark";
    return createTheme({
      typography: {
        fontFamily: '"Inter", sans-serif',
      },
      palette: {
        mode: isDark ? 'dark' : 'light',
        primary: {
          main: '#7e3af2', // purple
        },
        text: {
          primary: isDark ? '#f1f5f9' : '#111827',
          secondary: isDark ? '#94a3b8' : '#4b5563',
        },
        background: {
          default: isDark ? '#0f1117' : '#f3f4f6',
          paper: isDark ? '#1a1d27' : '#ffffff',
        },
        divider: isDark ? '#252836' : '#e5e7eb',
      },
      components: {
        MuiTextField: {
          defaultProps: {
            variant: 'outlined',
            size: 'small',
          },
          styleOverrides: {
            root: {
              marginTop: "16px",
              marginBottom: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "var(--bg-input)",
                "& fieldset": {
                  borderColor: "var(--border)",
                  borderWidth: "1.5px",
                },
                "&:hover fieldset": {
                  borderColor: "var(--text-muted)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "var(--border-focus)",
                  borderWidth: "2px",
                },
                "input, textarea": {
                  padding: "12px 14px",
                  fontSize: "0.875rem",
                  color: "var(--text-primary)",
                },
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.875rem",
                color: "var(--text-secondary)",
                fontWeight: 600,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "var(--border-focus)",
              },
            },
          },
        },
        MuiSelect: {
          defaultProps: {
            size: 'small',
          },
        },
        MuiFormControl: {
          styleOverrides: {
            root: {
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "var(--bg-input)",
                "& fieldset": {
                  borderColor: "var(--border)",
                  borderWidth: "1.5px",
                },
                "&:hover fieldset": {
                  borderColor: "var(--text-muted)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "var(--border-focus)",
                  borderWidth: "2px",
                },
                "& .MuiSelect-select": {
                  color: "var(--text-primary)",
                },
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.875rem",
                color: "var(--text-secondary)",
                fontWeight: 600,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "var(--border-focus)",
              },
            }
          }
        }
      },
    });
  }, [theme]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <Suspense fallback={<ThemedSuspense />}>
        <Windmill>
          <GoogleOAuthProvider clientId={CLIENT_ID}>
            <App />
          </GoogleOAuthProvider>
        </Windmill>
      </Suspense>
    </MuiThemeProvider>
  );
}

ReactDOM.render(
  <ThemeProvider>
    <SidebarProvider>
      <MainApp />
    </SidebarProvider>
  </ThemeProvider>,
  document.getElementById("root")
);

serviceWorker.register();


